import React from "react";
import * as THREE from "three";
import { observer } from "mobx-react-lite";
import { FloorplannerStoreContext } from "../../../../store/floorplannerStore";
import { WallType } from "../../../../types/floorplannerTypes";
import CatalogStandardMaterial from "../../materials/CatalogStandardMaterial";
import LitPatternMaterial from "../../materials/LitPatternMaterial";
import { WALL_FLOOR_CATALOG_TEXTURE_SIZE } from "../../materials/textureCache";
import {
  getBuiltInFurnitureMaterial,
  isGlassBuiltInFurnitureMaterial,
} from "../../materials/builtInFurnitureMaterials";
import { resolveMaterial } from "../../materials/materialResolver";
import { getWallColor } from "./wallGeometry";
import { resolveFillOpacity } from "../../../../utils/fillOpacity";

type Wall3DMaterialProps = {
  wall: WallType;
  color?: THREE.Color;
};

export function hasAssignedWallMaterial(wall: WallType): boolean {
  return Boolean(wall.threeD?.surfaceRef?.id || wall.threeD?.materialId);
}

export function shouldRenderWallPattern(wall: WallType): boolean {
  return Boolean(wall.fillPattern) && !hasAssignedWallMaterial(wall);
}

/**
 * Resolve the Ready / built-in material currently assigned to a wall.
 */
export function getAssignedWallBuiltInMaterial(wall: WallType) {
  return getBuiltInFurnitureMaterial(
    wall.threeD?.materialId ?? wall.threeD?.surfaceRef?.id,
  );
}

const Wall3DMaterial: React.FC<Wall3DMaterialProps> = observer(
  ({ wall, color }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const renderPattern = shouldRenderWallPattern(wall);
    const fillOpacity = resolveFillOpacity(wall);
    const fillIsTransparent = fillOpacity < 1;
    const assignedBuiltIn = getAssignedWallBuiltInMaterial(wall);
    const assignedIsGlass = isGlassBuiltInFurnitureMaterial(assignedBuiltIn);
    const surfaceRef = wall.threeD?.surfaceRef;
    const resolved = resolveMaterial("wall", wall, {
      defaultColor: getWallColor(wall),
    });
    const resolvedColor = color ?? resolved.color;
    const presetDefaults = assignedBuiltIn
      ? {
          roughness: assignedBuiltIn.roughness,
          metalness: assignedBuiltIn.metalness,
          // Admin / category tiles-per-metre when surfaceRef has no override.
          ...(assignedBuiltIn.repeatScale !== undefined
            ? { repeatScale: assignedBuiltIn.repeatScale }
            : {}),
        }
      : floorplannerStore.threeDMaterialDefaults.wall;

    if (renderPattern && wall.fillPattern) {
      return (
        <LitPatternMaterial
          fillColor={resolvedColor}
          fillPattern={wall.fillPattern}
          uvChannel={1}
          roughness={
            floorplannerStore.threeDMaterialDefaults.wall?.roughness ?? 0.78
          }
          metalness={
            floorplannerStore.threeDMaterialDefaults.wall?.metalness ?? 0
          }
          side={THREE.DoubleSide}
          opacity={fillOpacity}
          transparent={fillIsTransparent}
          depthWrite={!fillIsTransparent}
        />
      );
    }

    // Glass Ready presets need MeshPhysicalMaterial transmission (same as shapes).
    if (hasAssignedWallMaterial(wall) && assignedIsGlass && assignedBuiltIn) {
      return (
        <meshPhysicalMaterial
          color={resolvedColor}
          roughness={surfaceRef?.roughness ?? assignedBuiltIn.roughness}
          metalness={0}
          transmission={assignedBuiltIn.transmission ?? 1}
          thickness={assignedBuiltIn.thickness ?? 0.05}
          ior={assignedBuiltIn.ior ?? 1.5}
          transparent
          opacity={fillOpacity < 1 ? fillOpacity : 1}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      );
    }

    return (
      <CatalogStandardMaterial
        // Remount when Ready material is cleared so a prior albedo map cannot stick.
        key={surfaceRef?.id ?? wall.threeD?.materialId ?? "wall-default"}
        color={resolvedColor}
        surfaceRef={surfaceRef}
        defaults={presetDefaults}
        // Metre-space uv1 so wallpaper tiles every X metres instead of stretching.
        uvChannel={1}
        maxTextureSize={WALL_FLOOR_CATALOG_TEXTURE_SIZE}
        forceTextureWidth
        side={THREE.DoubleSide}
        opacity={fillOpacity}
        transparent={fillIsTransparent}
        depthWrite={!fillIsTransparent}
      />
    );
  },
);

export default Wall3DMaterial;
